/**
 * The one mutable config row — every risk limit in the product, on one screen.
 *
 * FOUR THINGS THIS SCREEN IS RESPONSIBLE FOR, all of which are ways a config
 * editor gets an operator into trouble:
 *
 * 1. ZERO IS A POSTURE, NOT "UNLIMITED", and the page must say so in words.
 *    On the eight delayed-price ceilings and the liquidity share, zero means
 *    REFUSE ALL — it is the emergency lever that closes delayed-price betting
 *    book-wide in seconds without a deploy. An operator who reads a blank-ish
 *    `0` as "no limit" and saves it has done the exact opposite of what they
 *    intended. So every zero-capable field renders a live, explicit reading of
 *    its current value back at the operator.
 *
 * 2. THE VERSION TOKEN IS THE WHOLE POINT. `expectedVersion` travels with the
 *    write; the server refuses with 409 when another console saved first. That
 *    409 is NOT an error to apologise for — it is the guard working — so it
 *    renders as its own state, with the newer row's version shown, rather than
 *    as a red failure the operator reads as a bug in the console.
 *
 * 3. ONLY CHANGED FIELDS ARE SENT. The audit log records `changedFields`, and
 *    posting the whole row on every save would make every audit entry claim
 *    every limit was touched — which destroys the one property the audit exists
 *    for: an accountable author for each specific risk decision.
 *
 * 4. THE NOTE IS PROMPTED FOR, not optional-by-omission. Config writes here are
 *    commercial decisions. A blank reason six weeks later is indistinguishable
 *    from a mistake.
 *
 * `hiddenMarkets` is deliberately NOT editable here — it is a whole-set
 * replacement with its own grammar, and it has its own screen with the cell
 * grid that can show what a wildcard is actually covering.
 */
import { useMemo, useState } from 'react';
import { useAdminParams, useUpdateAdminParams } from '@/hooks/useAdmin';
import { ApiClientError } from '@/api/client';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { AdminLoading, AdminError, AdminPageHeader } from './AdminAsync';
import type { AdminParams } from '@/types/api';
/*
  The field table, the zero-reading, the changed-fields diff and the save-body
  construction all live in `./logic` as pure functions, so the three rules that
  cost accountability if they are wrong — zero reads as a posture, only changed
  fields are sent, a blank input is not a change — are tested as data in, data
  out rather than through rendered markup.
*/
import {
  ALL_FIELDS,
  GROUPS,
  buildSavePayload,
  changedFields,
  readBack,
  unitSuffix,
  validateDraft,
} from './logic';

export function ParamsPage() {
  const { data, isLoading, isError, error, refetch } = useAdminParams();

  if (isLoading) return <AdminLoading label="Reading config" />;
  if (isError || !data) return <AdminError error={error} onRetry={() => void refetch()} />;

  /*
    KEYED ON VERSION so the draft is seeded ONCE per loaded version. React
    discards and remounts the form when the version changes, which is how the
    draft gets re-seeded without an effect writing state during render. Seeding
    on every render of fresh data would silently discard an in-progress edit the
    moment React Query refetched in the background.
  */
  return <ParamsForm key={data.version} data={data} refetch={() => void refetch()} />;
}

function ParamsForm({ data, refetch }: { data: AdminParams; refetch: () => void }) {
  const update = useUpdateAdminParams();

  const [draft, setDraft] = useState<Record<string, string>>(() => {
    const next: Record<string, string> = {};
    for (const f of ALL_FIELDS) next[f.key] = String(data[f.key] ?? 0);
    return next;
  });
  const [referralActive, setReferralActive] = useState(data.referralActive);
  const [note, setNote] = useState('');
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const changed = useMemo(
    () => changedFields(draft, referralActive, data),
    [draft, referralActive, data],
  );

  const invalid = useMemo(() => validateDraft(draft), [draft]);

  const conflict =
    update.error instanceof ApiClientError && update.error.status === 409 ? update.error : null;
  const otherError = update.error && !conflict ? update.error : null;
  const hasInvalid = Object.keys(invalid).length > 0;

  function onSave() {
    /*
      ONLY CHANGED FIELDS GO OUT. The server records `changedFields` on the audit
      row, so sending the whole form would attribute every untouched limit to
      whoever last pressed save — an audit trail that names the wrong author is
      worse than none. `expectedVersion` is the one always-present field.
    */
    update.mutate(buildSavePayload({ data, draft, changed, referralActive, note }), {
      onSuccess: () => setSavedAt(Date.now()),
    });
  }

  return (
    <>
      <AdminPageHeader
        title="Risk limits"
        subtitle="Every limit here is audit-logged against your account and takes effect immediately — no deploy. Zero is a posture, not “unlimited”; read each field’s plain-English line before saving."
        actions={
          <span className="rounded-md border border-white/15 bg-white/[0.05] px-2.5 py-1 text-[11px] font-medium tabular-nums text-white/55">
            version {data.version}
          </span>
        }
      />

      {conflict && (
        <div className="mb-5 rounded-2xl border border-brand-gold/35 bg-brand-gold/[0.07] p-4">
          <p className="text-sm font-semibold text-brand-gold">Someone else saved while you were editing</p>
          <p className="mt-1.5 text-sm leading-relaxed text-white/65">
            Nothing was written. Your version {data.version} is no longer current, so the whole
            request was refused rather than partly applied. Reload to see their change, then
            re-apply yours on top.
          </p>
          <Button variant="secondary" size="sm" className="mt-3" onClick={() => void refetch()}>
            Reload current values
          </Button>
        </div>
      )}

      {otherError && (
        <div className="mb-5">
          <AdminError error={otherError} />
        </div>
      )}

      {savedAt !== null && changed.length === 0 && !conflict && (
        <div className="mb-5 rounded-2xl border border-brand-green/30 bg-brand-green/[0.07] px-4 py-3 text-sm text-brand-green">
          Saved. Now at version {data.version}, recorded in the audit log.
        </div>
      )}

      <div className="space-y-5">
        {GROUPS.map((group) => (
          <Card key={group.title}>
            <h2 className="font-display text-sm font-bold tracking-tight">{group.title}</h2>
            <p className="mt-1.5 text-sm leading-relaxed text-white/45">{group.blurb}</p>

            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              {group.fields.map((f) => {
                const raw = draft[f.key] ?? '';
                const err = invalid[f.key];
                const isChanged = changed.includes(f.key);
                const n = Number(raw);
                return (
                  <div key={f.key}>
                    <label
                      htmlFor={f.key}
                      className="flex items-center gap-2 text-sm font-medium text-white/80"
                    >
                      {f.label}
                      {isChanged && (
                        <span className="rounded bg-brand-teal/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.08em] text-brand-teal">
                          edited
                        </span>
                      )}
                    </label>
                    <div className="mt-1 flex items-center gap-2">
                      <input
                        id={f.key}
                        inputMode="numeric"
                        value={raw}
                        onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                        className={`w-full rounded-lg border bg-brand-surface px-3 py-2 tabular-nums text-white focus:outline-none focus:ring-2 focus:ring-brand-teal ${
                          err ? 'border-red-500' : isChanged ? 'border-brand-teal/50' : 'border-white/20'
                        }`}
                      />
                      {unitSuffix(f.unit) && (
                        <span className="shrink-0 text-xs text-white/30">{unitSuffix(f.unit)}</span>
                      )}
                    </div>
                    {err ? (
                      <p className="mt-1 text-xs text-red-400">{err}</p>
                    ) : (
                      <p
                        className={`mt-1 text-xs ${
                          n === 0 && f.zeroMeans ? 'font-medium text-brand-gold' : 'text-white/55'
                        }`}
                      >
                        {readBack(n, f)}
                      </p>
                    )}
                    {f.help && <p className="mt-1 text-xs leading-relaxed text-white/30">{f.help}</p>}
                  </div>
                );
              })}
            </div>

            {group.title === 'Referrals' && (
              <label className="mt-4 flex items-start gap-3 rounded-xl border border-white/[0.08] bg-white/[0.02] p-3">
                <input
                  type="checkbox"
                  checked={referralActive}
                  onChange={(e) => setReferralActive(e.target.checked)}
                  className="mt-0.5 h-4 w-4 accent-[#2ec5b6]"
                />
                <span className="text-sm">
                  <span className="font-medium text-white/85">Referral programme active</span>
                  <span className="mt-0.5 block text-xs text-white/40">
                    Off means attributions are still captured but no reward is issued.
                  </span>
                </span>
              </label>
            )}
          </Card>
        ))}
      </div>

      {/* Sticky footer: the save control must be reachable without scrolling
          back up from whichever group the operator was editing. */}
      <div className="sticky bottom-0 mt-5 rounded-2xl border border-white/[0.09] bg-brand-navy2/95 p-4 backdrop-blur">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="min-w-[240px] flex-1">
            <label htmlFor="params-note" className="text-sm font-medium text-white/80">
              Why are you changing this?
            </label>
            <input
              id="params-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Recorded in the audit log against your account"
              className="mt-1 w-full rounded-lg border border-white/20 bg-brand-surface px-3 py-2 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-brand-teal"
            />
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-white/40">
              {changed.length === 0
                ? 'No changes'
                : `${changed.length} field${changed.length === 1 ? '' : 's'} changed`}
            </span>
            <Button
              onClick={onSave}
              loading={update.isPending}
              disabled={changed.length === 0 || hasInvalid || !!conflict}
            >
              Save changes
            </Button>
          </div>
        </div>
        {hasInvalid && (
          <p className="mt-2 text-xs text-red-400">
            Fix the highlighted fields first — every maximum here mirrors a database CHECK, so a
            value above it would be refused at the constraint rather than at the form.
          </p>
        )}
      </div>
    </>
  );
}
